import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';

import ResultsStore from '../../../../stores/ResultsStore';
import SearchStore from '../../../../stores/SearchStore';

import DealActions from '../../../../actions/DealActions';
import HotelActions from '../../../../actions/HotelActions';

import Currencies from '../../../../utils/Currencies';
import RouterWrapper from '../../../../utils/RouterWrapper';

import DealUtils from '../../../../entities/DealUtils';

import { Tooltip, Button } from '@mui/material';
import DealPricePerNight from './DealPricePerNight';
import DealPriceContainer from './DealPriceContainer';

import Analytics from 'arbitrip-common/client/analytics';
import PointsStore from '../../../../stores/PointsStore';
import ProfileStore from '../../../../stores/ProfileStore';
import { withRouter } from '../../../../utils/withRouter';
import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';
import { getPointsDiscountValue } from 'arbitrip-common/client/utils/PointsUtils';
import { getPointsToggleState } from 'arbitrip-common/client/analytics/services/loyaltyService';
import { isIsracardCompany } from '@/utils/isCompany';
import { HIGH_PRICE_MARKUP } from 'arbitrip-common/client/constants/Prices';
import BadgeOfferIsracard from '../../../general/BadgeOfferIsracard';

class RepresentativeDealComponent extends React.Component {
    constructor(props) {
        super(props);

        const points = PointsStore.getPoints();
        const preference = ResultsStore.getShowPricesInPoints();
        const profile_for_points = ProfileStore.getProfile();
        const isAnon = Boolean(profile_for_points && profile_for_points.anonymous && !profile_for_points.isLoggedIn);
        const anonPointsExRate = PointsStore.getAnonPointsExRate(profile_for_points?.display_currency || 'USD');
        const anonPointsOnly = preference && isAnon && anonPointsExRate > 0;

        this.state = {
            travel_policy: ResultsStore.getTravelPolicy(),
            points,
            showPricesWithPoints: preference && points.show && points.balance > 0,
            anonPointsOnly,
            anonPointsExRate,
        };

        this._onStoreChange = this._onStoreChange.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.arbitripPointsApplied !== this.props.arbitripPointsApplied) {
            this.setState({ busy_applying_points: true });
            const _self = this;
            setTimeout(() => {
                _self.setState({ busy_applying_points: false });
            }, 1000);
        }
    }

    componentDidMount() {
        PointsStore.addChangeListener(this._onStoreChange);
        ResultsStore.addChangeListener(this._onStoreChange);
    }

    componentWillUnmount() {
        PointsStore.removeChangeListener(this._onStoreChange);
        ResultsStore.removeChangeListener(this._onStoreChange);
    }

    _onStoreChange() {
        const points = PointsStore.getPoints();
        const preference = ResultsStore.getShowPricesInPoints();
        const profileForPoints = ProfileStore.getProfile();
        const isAnon = Boolean(profileForPoints && profileForPoints.anonymous && !profileForPoints.isLoggedIn);
        const anonPointsExRate = PointsStore.getAnonPointsExRate(profileForPoints?.display_currency || 'USD');
        const anonPointsOnly = preference && isAnon && anonPointsExRate > 0;

        this.setState({
            points,
            showPricesWithPoints: preference && points.show && points.balance > 0,
            anonPointsOnly,
            anonPointsExRate,
        });
    }

    handleOnSelectClick = () => {
        const { profile, searchTerms, hotel, deal, searchToken } = this.props;

        if (this.props.onAnonymousElalAttempt && this.props.onAnonymousElalAttempt()) {
			return;
		}

		HotelActions.getHotelData(hotel);
		if (deal) {
			DealActions.chooseDeal(deal);
		}

        const recommended_index = ResultsStore.calculateRecommendedIndex(hotel);
        HotelActions.setHotelRecommendedIndex(hotel.id, recommended_index);

        const recommended_count = ResultsStore.getRecommendedCount();
        const sort_method = ResultsStore.getSortMethod();
        const time_to_click_ml = ResultsStore.getTimePassedSinceSearchSessionCreatedInMilliSeconds();
        Analytics.actions.interactions.selectedHotel(
            profile,
            searchTerms.destination,
            searchTerms,
            recommended_index,
            recommended_count,
            sort_method,
            time_to_click_ml,
            undefined,
            hotel,
            getPointsToggleState(ResultsStore.getShowPricesInPoints()),
        );

        HotelActions.setHotelScroll(window.scrollY, hotel.id);
        profile.isLoggedIn
            ? window.open(RouterWrapper.buildHotelRoute(hotel, searchToken, searchTerms), '_blank')
            : this.props.navigate(RouterWrapper.buildHotelRoute(hotel, searchToken, searchTerms));
    };

    render() {
        const { t, deal, profile, arbitripPointsApplied, contractClientCompanyId, hotel } = this.props;
        const { points, showPricesWithPoints, anonPointsOnly, anonPointsExRate } = this.state;

        const noDeal = !DealUtils.isValidDeal(deal);

        if (noDeal) {
            return null;
        }

        const travelPolicy = this.state.travel_policy;
        const doesTravelPolicyExist = travelPolicy && (travelPolicy.default || travelPolicy.place);
        const isInPolicy = deal.in_policy;
        const travelPolicyCurrency = doesTravelPolicyExist ? Currencies.getSymbol(travelPolicy.currency) : '';
        const travelPolicyLocation =
            travelPolicy && travelPolicy.default
                ? t('ThisDestination')
                : travelPolicy && travelPolicy.place
                  ? travelPolicy.place.city || travelPolicy.place.state || travelPolicy.place.country
                  : '';
        const travelPolicyText = doesTravelPolicyExist
            ? deal.out_of_policy_message ||
              t('Policy.OutOfPolicyMessage', {
                  location: travelPolicyLocation,
                  price: travelPolicyCurrency + travelPolicy.price,
              })
            : null;

        const tooltip = (
            <div className="policy-tooltip-container">
                <span>{travelPolicyText}</span>
            </div>
        );

        const checkIn = DateUtils.parseFormatDate(deal.check_in);
        const checkOut = DateUtils.parseFormatDate(deal.check_out);
        const nights = checkOut.diff(checkIn, 'days');
        const isPrivateTravelOnly = !!_.get(profile, 'private_travel_only');

        const pointsDiscountValue = showPricesWithPoints ? getPointsDiscountValue(deal, profile, points) : 0;

        const expediaTotalPriceAfterDiscount = deal.expedia_pricing?.totalPrice - pointsDiscountValue;
        const totalPriceAfterDiscount = deal.totalPrice - pointsDiscountValue;
        const rooms = SearchStore.getRooms() || 1;

        const IS_IN_TRAVEL_POLICY = doesTravelPolicyExist && !PriceBreakdown.isBreakdownPrice({ profile, deal }) && (
            <Tooltip title={tooltip} placement="top-end" arrow>
                <div className="is-in-policy">
                    <div className={isInPolicy ? 'in-policy icon' : 'out-of-policy icon'}></div>
                    <div className={isInPolicy ? 'in-policy text' : 'out-of-policy text'}>
                        {isInPolicy ? t('InPolicy') : t('OutOfPolicy')}
                    </div>
                </div>
            </Tooltip>
        );

        const DEAL_ACTION = (
            <Button
                className="select-button tab-theme-color"
                onClick={this.handleOnSelectClick}
                variant="outline"
                disableRipple
                aria-label={`${t('Select')} ${hotel.name}`}
                role="link"
            >
                {t('Select')}
            </Button>
        );

        return (
            <>
                <div>
                    <div
                        className={
                            PriceBreakdown.isBreakdownPrice({ profile, deal })
                                ? 'price-wrapper bottom'
                                : 'price-wrapper'
                        }
                    >
                        <DealPriceContainer
                            deal={deal}
                            profile={profile}
                            arbitripPointsApplied={arbitripPointsApplied}
                            contractClientCompanyId={contractClientCompanyId}
                            busyApplyingPoints={this.state.busy_applying_points}
                            showPricesWithPoints={showPricesWithPoints}
                            points={points}
                            nights={nights}
                            pointsDiscountValue={pointsDiscountValue}
                            expediaTotalPriceAfterDiscount={expediaTotalPriceAfterDiscount}
                            totalPriceAfterDiscount={totalPriceAfterDiscount}
                            anonPointsOnly={anonPointsOnly}
                            anonPointsExRate={anonPointsExRate}
                            priceBeforeDiscount={
                                isIsracardCompany() && !profile.isLoggedIn
                                    ? totalPriceAfterDiscount * HIGH_PRICE_MARKUP
                                    : 0
                            }
                        />

                        {isIsracardCompany() && (
                            <React.Fragment>
                                <BadgeOfferIsracard style={{ marginBottom: '4px' }} />
                            </React.Fragment>
                        )}
                        <DealPricePerNight
                            deal={deal}
                            profile={profile}
                            nights={nights}
                            expediaTotalPriceAfterDiscount={expediaTotalPriceAfterDiscount}
                            totalPriceAfterDiscount={totalPriceAfterDiscount}
                            rooms={rooms}
                            isPrivateTravelOnly={isPrivateTravelOnly}
                        />

                        {IS_IN_TRAVEL_POLICY}
                    </div>
                </div>
                {DEAL_ACTION}
            </>
        );
    }
}

RepresentativeDealComponent.propTypes = {
    deal: PropTypes.any,
    hotel: PropTypes.any,
    searchToken: PropTypes.any,
    searchTerms: PropTypes.any,
	onAnonymousElalAttempt: PropTypes.func,
};

export default withTranslation()(withRouter(RepresentativeDealComponent));
